/*
 * The account routes a directory screen calls (protocol.md §3.2 "Accounts").
 *
 * The list a member reads comes out of the client model — the bootstrap fills it and
 * `account.updated` keeps it current — so nothing here is called to draw the directory. These two
 * calls exist for what the model cannot hold: one account at the *viewer's own rung*, which is where
 * a time zone and, for an admin, an address appear (security.md §9.6), and the whole list including
 * the deactivated accounts, which only the administration screen shows.
 */

import { api } from "../net/api.ts"
import type { PublicUser } from "../store/model.ts"

/**
 * One account as the server projected it for the caller. Every field beyond `PublicUser` is present
 * only on the rung that earns it, so a screen renders whatever came back and never decides for
 * itself who may see what.
 */
export interface PersonView extends PublicUser {
	email?: string
	locale?: string
	timezone?: string
	createdAt?: number
	lastLoginAt?: number
	deactivatedAt?: number
}

export const peopleApi = {
	/**
	 * The directory. `includeDeactivated` is the administration screen's view of it, and the flag is
	 * honoured for whoever sends it — `account.list` is a member's action. What protects the rows is
	 * the projection: a member receives the public fields of an account and never an address, a
	 * session or a device (security.md §9.6). Making the flag admin-only is its own change.
	 */
	list(includeDeactivated = false): Promise<PersonView[]> {
		const suffix = includeDeactivated ? "?includeDeactivated=true" : ""
		return api.get<{ users: PersonView[] }>(`/api/users${suffix}`).then((body) => body.users)
	},

	person(id: number): Promise<PersonView> {
		return api.get<{ user: PersonView }>(`/api/users/${id}`).then((body) => body.user)
	},
}
